import React, { useState, useEffect } from "react";
import { Input, Popover, Radio, Modal, message, Button, Spin } from "antd";
import { ArrowDownOutlined, DownOutlined, SettingOutlined } from "@ant-design/icons";
import axios from "axios";
import { ethers } from "ethers";
import { useNetworkContext } from './NetworkContext';
import TokenListModal from "./TokenListModal";
import { networks } from './networks'; // Make sure the path to networks.js is correct

function Swap(props) {
  const { address, isConnected } = props;
  const { provider, signer } = useNetworkContext();
  
  const [messageApi, contextHolder] = message.useMessage();
  const [slippage, setSlippage] = useState(2.5);
  const [tokenOneAmount, setTokenOneAmount] = useState(null);
  const [tokenTwoAmount, setTokenTwoAmount] = useState(null);
  const [tokenOne, setTokenOne] = useState(null);
  const [tokenTwo, setTokenTwo] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [changeToken, setChangeToken] = useState(1);
  const [prices, setPrices] = useState(null);
  const [txDetails, setTxDetails] = useState({
    to: null,
    data: null,
    value: null,
  });
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTransactionSent, setIsTransactionSent] = useState(false);
  const [transactionHash, setTransactionHash] = useState(null);

  const [chainId, setChainId] = useState(null);

  const network = networks[chainId];
  const blockExplorerUrl = network?.blockExplorerUrls[0];
  const blockExplorerName = network?.chainName;

  const [selectedTokenBalance, setSelectedTokenBalance] = useState(null);

  useEffect(() => {
    async function fetchChainId() {
      const network = await signer.provider.getNetwork();
      const chainId = network.chainId;
      setChainId(chainId);

      try {
        const response = await axios.post('/web3/update-chain-id/', { chainId });
        console.log('Chain ID updated:', response.data);
      } catch (error) {
        console.error('Failed to update chain ID:', error);
      }
    }

    fetchChainId();
  }, [signer]);

  useEffect(() => {
    if (chainId) {
      setTokenOne(null);  // Reset tokenOne on chainId change
      setTokenTwo(null);  // Reset tokenTwo on chainId change
      setTokenOneAmount(null);  // Reset tokenOneAmount on chainId change
      setTokenTwoAmount(null);  // Reset tokenTwoAmount on chainId change
    }
  }, [chainId]);
  console.log('Chain ID:', chainId);

  function handleSlippageChange(e) {
    setSlippage(e.target.value);
  }

  function changeAmount(value) {
    const amount = typeof value === 'string' ? value : value.target.value;
    setTokenOneAmount(amount);
    if (amount && prices) {
      setTokenTwoAmount((amount * prices.ratio).toFixed(4));
    } else {
      setTokenTwoAmount(null);
    }
  }

  function switchTokens() {
    setPrices(null);
    setTokenOneAmount(null);
    setTokenTwoAmount(null);
    const one = tokenOne;
    const two = tokenTwo;
    setTokenOne(two);
    setTokenTwo(one);

    
  }

  function openModal(asset) {
    setChangeToken(asset);
    setIsOpen(true);
  }

  function modifyToken(token, changeToken) {
    setPrices(null);
    setTokenOneAmount(null);
    setTokenTwoAmount(null);
    if (changeToken === 1) {
      setTokenOne(token);
    } else {
      setTokenTwo(token);
    }
  }

  useEffect(() => {
    if (tokenOne && tokenTwo && chainId) {
      fetchPrices(tokenOne.address, tokenTwo.address, chainId);
    }
  }, [tokenOne, tokenTwo, chainId]);

  async function fetchPrices(one, two, chainId) {
    try {
      const res = await axios.get('/web3/tokenPrice/', {
        params: { addressOne: one, addressTwo: two, chainId: chainId },
      });
      console.log("Response from backend:", res.data); // Debugging log
      setPrices(res.data);
    } catch (error) {
      console.error("Error fetching token prices:", error);
      messageApi.error("Failed to fetch token prices");
    }
  }

  async function fetchDexSwap() {
    setIsConfirming(true); // Show confirm modal
  }

  async function confirmSwap() {
    setIsConfirming(false); // Hide confirm modal
    setIsLoading(true); // Show loading spinner

    const tokenOneDecimals = tokenOne.decimals || 18; // Default to 18 if decimals are not set
    const amountInSmallestUnit = ethers.utils.parseUnits(tokenOneAmount.toString(), tokenOneDecimals).toString();

    try {
      const res = await axios.get(`/web3/dexSwap/`, {
        params: {
          tokenOneAddress: tokenOne.address,
          tokenTwoAddress: tokenTwo.address,
          amount: amountInSmallestUnit,
          address: address,
          slippage: slippage,
        },
      });

      if (res.data.message === "not approved") {
        setTxDetails(res.data.data);
        console.log("not approved");
        const tx = await signer.sendTransaction(res.data.data);
        console.log("Approve transaction sent: ", tx);
        messageApi.success("Token approved, now you can proceed with the swap.");

        await tx.wait();

        await confirmSwap();
        return;
      }

      const tx = res.data;
      if (!tx || Object.keys(tx).length === 0) {
        throw new Error("Received empty transaction details from the backend.");
      }

      console.log("Received transaction details:", tx);

      // Send the transaction
      const transaction = {
        from: address,
        to: tx.to,
        data: tx.data,
        value: ethers.utils.parseUnits(tx.value.toString(), "wei"),
        gasLimit: ethers.BigNumber.from(tx.gas),
        gasPrice: ethers.BigNumber.from(tx.gasPrice),
        chainId: chainId, // Ensure this is properly defined and set
      };

      console.log("Transaction:", transaction);
      setTransactionDetails(transaction);

      const response = await signer.sendTransaction(transaction);
      console.log("Transaction response:", response);

      setTransactionHash(response.hash);
      setIsLoading(false); // Hide loading spinner
      setIsTransactionSent(true); // Show transaction sent modal

      messageApi.success("Swap transaction completed successfully.");
    } catch (error) {
      console.error("Error during swap:", error);
      setIsLoading(false); // Hide loading spinner
      const errorMessage =
        error.response && error.response.data && error.response.data.description
          ? error.response.data.description
          : error.message || "An error occurred during the swap. Please try again.";
      messageApi.error(errorMessage);
    }
  }

  const settings = (
    <>
      <div>Slippage Tolerance</div>
      <div>
        <Radio.Group value={slippage} onChange={handleSlippageChange}>
          <Radio.Button value={0.5}>0.5%</Radio.Button>
          <Radio.Button value={2.5}>2.5%</Radio.Button>
          <Radio.Button value={5}>5.0%</Radio.Button>
        </Radio.Group>
      </div>
    </>
  );

  return (
    <>
      {contextHolder}
      <TokenListModal 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)} 
        onSelectToken={modifyToken} 
        changeToken={changeToken} 
        signer={signer} 
        chainId={chainId} 
        setSelectedTokenBalance={setSelectedTokenBalance} 
      />
  
      {/* Confirm Swap Modal */}
      <Modal
        open={isConfirming}
        onCancel={() => setIsConfirming(false)}
        footer={null}
        title="Confirm Swap"
        className="confirmSwapModal"
        bodyStyle={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '30px',
          width: '100%',
          boxSizing: 'border-box',
          margin: '0',
          minHeight: '200px',
          
        }}
      >
        <div className="modalText">You pay: {tokenOneAmount} {tokenOne?.ticker}</div>
        <div className="modalText">You receive: {tokenTwoAmount} {tokenTwo?.ticker}</div>
        <Button type="primary" className="modalButton" onClick={confirmSwap}>Confirm Swap</Button>
      </Modal>
  
      <Modal open={isLoading} 
              footer={null} 
              closable={false} 
              title="Confirm swap" 
              className="loadingModal"
              bodyStyle={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  padding: '30px',
                  width: '100%',
                  boxSizing: 'border-box',
                  margin: '0',
                  minHeight: '200px',
                   
                }}
              >
        <Spin tip="Please sign transaction in your wallet" className="loadingSpinner" />
      </Modal>
      
      <Modal
        open={isTransactionSent}
        onCancel={() => setIsTransactionSent(false)}
        footer={null}
        title="Transaction sent"
        className="transactionSentModal"
        bodyStyle={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '30px',
          width: '100%',
          boxSizing: 'border-box',
          margin: '0',
          minHeight: '200px',
        }}
      >
        <div className="modalText">Swap of {tokenOne?.ticker} to {tokenTwo?.ticker}</div>
        <Button
          type="primary"
          className="modalButton"
          href={`${blockExplorerUrl}/tx/${transactionHash}`}
          target="_blank"
        >
          View on Explorer
        </Button>
      </Modal>
  
      <div className="tradeBox">
        <div className="tradeBoxHeader">
          <h4>Swap (beta)</h4>
          <Popover content={settings} title="Settings" trigger="click" placement="bottomRight">
            <SettingOutlined className="cog" />
          </Popover>
        </div>
        <div className="inputs">
          <div className="inputContainer">
            <Input 
              placeholder="0" 
              value={tokenOneAmount} 
              onChange={changeAmount} 
              disabled={!prices} 
              className="inputField"
            />
            <div className="balanceMaxContainer">
            <span>
              Balance: {selectedTokenBalance ? Math.floor(parseFloat(selectedTokenBalance) * 100) / 100 : '0.00'}
            </span>
              <span 
                className="maxButton" 
                onClick={() => {
                  const maxAmount = Math.floor(parseFloat(selectedTokenBalance) * 100) / 100;
                  setTokenOneAmount(maxAmount);
                  changeAmount(maxAmount.toString());
                }}
              >
                Max
              </span>
            </div>
          </div>
          <Input 
            placeholder="0" 
            value={tokenTwoAmount} 
            disabled={true} 
          />
          <div className="switchButton" onClick={switchTokens}>
            <ArrowDownOutlined className="switchArrow" />
          </div>
          <div className="assetOne" onClick={() => openModal(1)}>
            <img src={tokenOne?.img}  className="assetLogo" />
            {tokenOne?.ticker}
            <DownOutlined className="dropdownArrow"/>
          </div>
           <div className="assetTwo" onClick={() => openModal(2)}>
              {tokenTwo ? <img src={tokenTwo.img} alt="assetTwoLogo" className="assetLogo" /> : null}
              {tokenTwo ? tokenTwo.ticker : <span className="selectTokenText">Select Token</span>}
              <DownOutlined className="dropdownArrow"/>
           </div>
        </div>
        <div className="swapButton" disabled={!tokenOneAmount || !isConnected} onClick={fetchDexSwap}>Swap</div>
      </div>
    </>
  );
}

export default Swap;